import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, PlayCircle, Headphones, ArrowLeft } from "lucide-react";

const getCourseId = (course) => course.id || course._id || course.vimeoProjectId;
const getModuleId = (module) =>
  module.id || module._id || module.vimeoProjectId || module.title;
const getSubtopicId = (subtopic) =>
  subtopic.id || subtopic._id || subtopic.vimeoProjectId || subtopic.title;
const getLessonId = (lesson) =>
  lesson.id || lesson._id || lesson.vimeoVideoId || lesson.vimeoVideoUri;

const COURSE_TITLE = "Mixing Masterclass";

const formatDuration = (duration) => {
  if (typeof duration !== "number") return duration;

  const minutes = Math.floor(duration / 60);
  const seconds = String(duration % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const stripModulePrefix = (title = "") =>
  title.replace(/^(?:Module\s*)?\d+\s*(?:\.|[-—])\s*/i, "");

const getCourseModules = (course) => {
  if (course.modules?.length) return course.modules;
  if (course.subtopics?.length) return course.subtopics;
  if (course.lessons?.length) return [course];
  return [];
};

const getLessonGroups = (module) =>
  module.subtopics?.length
    ? module.subtopics
    : [{ title: null, lessons: module.lessons || [] }];

const usesCourseShell = (items = []) => items.some((item) => item.modules?.length);

const countModuleLessons = (module) =>
  getLessonGroups(module).reduce(
    (total, group) => total + (group.lessons?.length || 0),
    0
  );

const getCourseLessonPosition = (course, activeLessonId) => {
  const lessons = getCourseModules(course).flatMap((module) =>
    getLessonGroups(module).flatMap((group) => group.lessons || [])
  );
  const activeIndex = lessons.findIndex(
    (lesson) => getLessonId(lesson) === activeLessonId
  );

  return {
    current: activeIndex >= 0 ? activeIndex + 1 : null,
    total: lessons.length,
  };
};

const findCourseByLessonId = (courses, activeLessonId) => {
  if (!activeLessonId) return courses[0] || null;

  return (
    courses.find((course) =>
      getCourseModules(course).some((module) =>
        getLessonGroups(module).some((group) =>
          (group.lessons || []).some(
            (lesson) => getLessonId(lesson) === activeLessonId
          )
        )
      )
    ) ||
    courses[0] ||
    null
  );
};

const findModuleByLessonId = (modules, activeLessonId) => {
  if (!activeLessonId) return modules[0] || null;

  return (
    modules.find((module) =>
      getLessonGroups(module).some((group) =>
        (group.lessons || []).some(
          (lesson) => getLessonId(lesson) === activeLessonId
        )
      )
    ) ||
    modules[0] ||
    null
  );
};

const findModuleIndex = (course, activeModule) =>
  getCourseModules(course).findIndex(
    (module) => getModuleId(module) === getModuleId(activeModule)
  );

const getFlatCourse = (modules) => ({
  id: "course-modules",
  title: COURSE_TITLE,
  modules,
});

const CourseModules = ({
  course,
  activeLessonId,
  onSelect,
  closeOnSelect = false,
  onClose,
  testPrefix = "",
}) => {
  const courseModules = useMemo(() => getCourseModules(course), [course]);
  const [openModules, setOpenModules] = useState(() =>
    courseModules.reduce(
      (acc, module) => ({ ...acc, [getModuleId(module)]: true }),
      {}
    )
  );
  const [openGroups, setOpenGroups] = useState(() =>
    courseModules.reduce((acc, module) => {
      getLessonGroups(module).forEach((group) => {
        if (group.title && group.title !== module.title) {
          acc[`${getModuleId(module)}-${getSubtopicId(group)}`] = true;
        }
      });

      return acc;
    }, {})
  );

  useEffect(() => {
    setOpenModules((prev) =>
      courseModules.reduce(
        (acc, module) => ({
          ...acc,
          [getModuleId(module)]: acc[getModuleId(module)] ?? true,
        }),
        prev
      )
    );
  }, [courseModules]);

  const toggle = (id) =>
    setOpenModules((prev) => ({ ...prev, [id]: !prev[id] }));
  const toggleGroup = (id) =>
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="mt-1 space-y-2">
      {courseModules.map((module, mi) => {
        const moduleId = getModuleId(module);
        const isOpen = openModules[moduleId];
        const lessonGroups = getLessonGroups(module);

        return (
          <div
            key={moduleId}
            data-testid={`${testPrefix}module-${moduleId}`}
            className="mb-2"
          >
            <button
              data-testid={`${testPrefix}module-toggle-${moduleId}`}
              onClick={() => toggle(moduleId)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-white/[0.03] transition-colors group"
            >
              <div className="flex items-center gap-3 min-w-0">
                <span className="font-heading text-[11px] text-[#C5A059] font-semibold tracking-wider w-5">
                  {String(mi + 1).padStart(2, "0")}
                </span>
                <span className="text-sm font-medium text-zinc-200 truncate">
                  {stripModulePrefix(module.title)}
                </span>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-zinc-500 transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
                strokeWidth={1.7}
              />
            </button>

            {isOpen && (
              <div className="mt-1 ml-3 pl-4 border-l border-white/[0.06] space-y-3">
                {lessonGroups.map((group) => {
                  const hasSubtopic = group.title && group.title !== module.title;
                  const groupId = `${moduleId}-${getSubtopicId(group)}`;
                  const groupOpen = openGroups[groupId] ?? true;
                  const lessonList = (
                    <ul className="space-y-0.5">
                      {(group.lessons || []).map((lesson) => {
                        const lessonId = getLessonId(lesson);
                        const active = lessonId === activeLessonId;

                        return (
                          <li key={lessonId}>
                            <button
                              data-testid={`lesson-item-${lessonId}`}
                              onClick={() => {
                                onSelect(lesson);
                                if (closeOnSelect) onClose?.();
                              }}
                              className={`w-full text-left flex items-start gap-3 pl-4 pr-3 py-2.5 rounded-md transition-all duration-200 ${
                                active
                                  ? "bg-white/[0.05] border-l-2 border-[#C5A059] -ml-[1px] text-white"
                                  : "border-l-2 border-transparent -ml-[1px] text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.02]"
                              }`}
                            >
                              <PlayCircle
                                className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                                  active ? "text-[#C5A059]" : "text-zinc-600"
                                }`}
                                strokeWidth={1.5}
                              />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium leading-snug truncate">
                                  {lesson.title}
                                </div>
                                <div className="text-[11px] text-zinc-600 mt-0.5">
                                  {formatDuration(lesson.duration)}
                                </div>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  );

                  return (
                    <div key={getSubtopicId(group)}>
                      {hasSubtopic ? (
                        <>
                          <button
                            type="button"
                            onClick={() => toggleGroup(groupId)}
                            className="w-full flex items-center justify-between px-4 pb-1 pt-2 text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-300 font-semibold transition-colors"
                          >
                            <span className="truncate">{group.title}</span>
                            <ChevronDown
                              className={`w-3.5 h-3.5 text-zinc-600 transition-transform ${
                                groupOpen ? "rotate-180" : ""
                              }`}
                              strokeWidth={1.7}
                            />
                          </button>
                          {groupOpen && lessonList}
                        </>
                      ) : (
                        lessonList
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export const Sidebar = ({ modules, activeLessonId, onSelect }) => {
  const hasCourseShell = usesCourseShell(modules);
  const flatCourse = useMemo(() => getFlatCourse(modules), [modules]);
  const activeCourse = hasCourseShell
    ? findCourseByLessonId(modules, activeLessonId)
    : flatCourse;
  const activeModule = activeCourse
    ? findModuleByLessonId(getCourseModules(activeCourse), activeLessonId)
    : null;
  const activeModuleIndex =
    activeCourse && activeModule ? findModuleIndex(activeCourse, activeModule) : -1;
  const activeModuleNumber =
    activeModuleIndex >= 0 ? String(activeModuleIndex + 1).padStart(2, "0") : null;
  const activeLessonPosition = activeCourse
    ? getCourseLessonPosition(activeCourse, activeLessonId)
    : { current: null, total: 0 };
  const [openCourses, setOpenCourses] = useState(() =>
    modules.reduce((acc, course) => {
      acc[getCourseId(course)] = true;
      return acc;
    }, {})
  );

  useEffect(() => {
    setOpenCourses((prev) =>
      modules.reduce(
        (acc, course) => ({
          ...acc,
          [getCourseId(course)]: acc[getCourseId(course)] ?? true,
        }),
        prev
      )
    );
  }, [modules]);

  const toggle = (id) =>
    setOpenCourses((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <aside
      data-testid="dashboard-sidebar"
      className="hidden lg:flex flex-col w-80 shrink-0 h-screen sticky top-0 border-r border-white/[0.06] bg-[#050505]"
    >
      <div className="px-6 py-6 border-b border-white/[0.06] flex items-center justify-between">
        <Link to="/" data-testid="sidebar-logo" className="flex items-center gap-2.5 group">
          <span className="w-8 h-8 rounded-lg bg-[#C5A059]/10 border border-[#C5A059]/30 flex items-center justify-center">
            <Headphones className="w-3.5 h-3.5 text-[#C5A059]" strokeWidth={1.6} />
          </span>
          <span className="font-heading text-base font-semibold text-white">
            Kiru<span className="text-[#C5A059]">·</span>beezy
          </span>
        </Link>
        <Link
          to="/"
          data-testid="sidebar-back-home"
          className="text-zinc-500 hover:text-zinc-200 transition-colors"
          aria-label="Back to landing"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.7} />
        </Link>
      </div>

      <div className="px-6 py-5 border-b border-white/[0.06]">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] font-semibold">
          Now playing
        </div>
        <div className="mt-2 flex items-start gap-3">
          {activeModuleNumber && (
            <span className="font-heading text-sm text-[#C5A059] font-semibold tracking-wider pt-0.5">
              {activeModuleNumber}
            </span>
          )}
          <div className="min-w-0">
            <div className="font-heading text-lg text-white font-medium leading-tight">
              {activeModule?.title
                ? stripModulePrefix(activeModule.title)
                : activeCourse?.title || "Course"}
            </div>
            {activeCourse && (
              <div className="text-xs text-zinc-500 mt-1">
                {activeLessonPosition.current
                  ? `Lesson ${activeLessonPosition.current} of ${activeLessonPosition.total}`
                  : `${activeLessonPosition.total} lessons`}
                {/* {activeModule ? ` · ${countModuleLessons(activeModule)} in this module` : ""} */}
              </div>
            )}
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto sidebar-scroll px-3 py-4">
        {hasCourseShell ? modules.map((course) => {
          const courseId = getCourseId(course);
          const isOpen = openCourses[courseId];

          return (
            <div key={courseId} data-testid={`course-${courseId}`} className="mb-3">
              <button
                data-testid={`course-toggle-${courseId}`}
                onClick={() => toggle(courseId)}
                className="w-full flex items-center justify-between px-3 py-3 rounded-lg bg-white/[0.025] hover:bg-white/[0.05] border border-white/[0.06] transition-colors group"
              >
                <div className="min-w-0 text-left">
                  <span className="block text-[10px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">
                    Course
                  </span>
                  <span className="block mt-1 text-sm font-medium text-zinc-200 truncate">
                    {course.title}
                  </span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-zinc-500 transition-transform ${
                    isOpen ? "rotate-180" : ""
                  }`}
                  strokeWidth={1.7}
                />
              </button>

              {isOpen && (
                <div className="mt-2">
                  <CourseModules
                    course={course}
                    activeLessonId={activeLessonId}
                    onSelect={onSelect}
                  />
                </div>
              )}
            </div>
          );
        }) : (
          <CourseModules
            course={flatCourse}
            activeLessonId={activeLessonId}
            onSelect={onSelect}
          />
        )}
      </nav>

      <div className="px-6 py-5 border-t border-white/[0.06]">
        <div className="text-xs text-zinc-500">Kiran Solanki</div>
        <div className="text-[11px] text-zinc-600 mt-0.5">Lead Instructor</div>
      </div>
    </aside>
  );
};

export const MobileSidebar = ({ modules, activeLessonId, onSelect, open, onClose }) => {
  const hasCourseShell = usesCourseShell(modules);
  const flatCourse = useMemo(() => getFlatCourse(modules), [modules]);
  const [openCourses, setOpenCourses] = useState(() =>
    modules.reduce((acc, course) => ({ ...acc, [getCourseId(course)]: true }), {})
  );

  useEffect(() => {
    setOpenCourses((prev) =>
      modules.reduce(
        (acc, course) => ({
          ...acc,
          [getCourseId(course)]: acc[getCourseId(course)] ?? true,
        }),
        prev
      )
    );
  }, [modules]);

  const toggle = (id) =>
    setOpenCourses((prev) => ({ ...prev, [id]: !prev[id] }));

  if (!open) return null;

  return (
    <div data-testid="mobile-sidebar" className="lg:hidden fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-80 max-w-[85vw] h-full bg-[#050505] border-r border-white/[0.06] overflow-y-auto sidebar-scroll">
        <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
          <span className="font-heading text-base font-semibold text-white">
            Curriculum
          </span>
          <button
            data-testid="mobile-sidebar-close"
            onClick={onClose}
            className="text-zinc-400 hover:text-white text-sm"
          >
            Close
          </button>
        </div>
        <div className="p-3">
          {hasCourseShell ? modules.map((course) => {
            const courseId = getCourseId(course);
            const isOpen = openCourses[courseId];

            return (
              <div key={courseId} data-testid={`mobile-course-${courseId}`} className="mb-3">
                <button
                  data-testid={`mobile-course-toggle-${courseId}`}
                  onClick={() => toggle(courseId)}
                  className="w-full flex items-center justify-between px-3 py-3 rounded-lg bg-white/[0.025] hover:bg-white/[0.05] border border-white/[0.06]"
                >
                  <div className="min-w-0 text-left">
                    <span className="block text-[10px] uppercase tracking-[0.2em] text-[#C5A059] font-semibold">
                      Course
                    </span>
                    <span className="block mt-1 text-sm font-medium text-zinc-200 truncate">
                      {course.title}
                    </span>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-zinc-500 transition-transform ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <div className="mt-2">
                    <CourseModules
                      course={course}
                      activeLessonId={activeLessonId}
                      onSelect={onSelect}
                      closeOnSelect
                      onClose={onClose}
                      testPrefix="mobile-"
                    />
                  </div>
                )}
              </div>
            );
          }) : (
            <CourseModules
              course={flatCourse}
              activeLessonId={activeLessonId}
              onSelect={onSelect}
              closeOnSelect
              onClose={onClose}
              testPrefix="mobile-"
            />
          )}
        </div>
      </div>
    </div>
  );
};
